import { createField, type ScalarField } from './grid.ts';
import { EVOLUTION_MODEL, evolveColumns, solarMeltFactor } from './evolution.ts';
import type { StormPrecipitation } from './precipitation.ts';
import type { InitialSnowParams } from './snowcover.ts';
import {
  addLayer,
  columnDepth,
  Grain,
  splitLayer,
  WEAK_LAYER_GRAINS,
  type SnowColumns,
} from './snowpack.ts';
import { computeStability, STABILITY_MODEL } from './stability.ts';
import { runStorm, type StormBudget, type StormResult } from './storm.ts';
import { temperatureAt, type TemperatureProfile } from './temperature.ts';
import type { Topography } from './topography.ts';
import { aspectSector } from './weaklayers.ts';

/**
 * A scenario's weather as a sequence of events, one after another:
 *
 * - `storm`: new snow and/or wind (drifting) over its duration — wind-only is 0 cm of snow; with
 *   warm temperatures the precipitation falls as rain.
 * - `fair`: calm weather. With a clear sky and cold air, surface hoar grows on shaded slopes and a
 *   thin, cold snowpack faces near the surface; warm air melts the surface and cold nights
 *   refreeze it into crusts (both via the snowpack evolution).
 *
 * Every event carries its own temperature profile. The snowpack settles, bonds and ages through
 * all of it, so weak layers can form from the weather history instead of only being placed.
 */
export type TimelineEventKind = 'storm' | 'fair';

interface EventBase {
  id: string;
  kind: TimelineEventKind;
  durationHours: number;
  temperature: TemperatureProfile;
}

export interface StormTimelineEvent extends EventBase, StormPrecipitation {
  kind: 'storm';
  windSpeed: number;
  /** Direction the wind blows from, true bearing (deg). */
  windFromDeg: number;
  inflow: 'equilibrium' | 'none';
}

export interface FairTimelineEvent extends EventBase {
  kind: 'fair';
  /** Clear sky: radiative cooling at night — surface hoar and near-surface facets. */
  clearSky: boolean;
}

export type TimelineEvent = StormTimelineEvent | FairTimelineEvent;

export const FAIR_MODEL = {
  /** Time step (h) for fair-weather events. */
  stepHours: 3,
  /** Surface hoar growth per clear night (m of 150 kg/m³ hoar). */
  surfaceHoarPerDay: 0.003,
  surfaceHoarDensity: 150,
  /** It grows on shaded aspects (N, NE, E, NW sectors), on slopes up to this steep… */
  surfaceHoarSectors: [0, 1, 2, 7] as readonly number[],
  surfaceHoarMaxSlope: 50,
  /** …where the air is below this (°C). */
  surfaceHoarMaxTemp: -1,
  /** Near-surface faceting: clear sky, air below this (°C)… */
  facetMaxTemp: -5,
  /** …a snowpack thinner than this (m) — a strong temperature gradient… */
  facetMaxDepth: 1.5,
  /** …for this long (h) turns the top `facetLayerDepth` m into facets. */
  facetHours: 72,
  facetLayerDepth: 0.2,
  /** Only snow lighter than this faces (kg/m³); dense slabs and crusts don't. */
  facetMaxDensity: 300,
} as const;

/** The event active at `hour` (the last one if past the end), with its start hour. */
export function eventAt(
  events: readonly TimelineEvent[],
  hour: number,
): { event: TimelineEvent; start: number } | null {
  let start = 0;
  for (const e of events) {
    const d = Math.max(1, Math.round(e.durationHours));
    if (hour < start + d) return { event: e, start };
    start += d;
  }
  const last = events[events.length - 1];
  return last ? { event: last, start: start - Math.max(1, Math.round(last.durationHours)) } : null;
}

export function totalHours(events: readonly TimelineEvent[]): number {
  return events.reduce((h, e) => h + Math.max(1, Math.round(e.durationHours)), 0);
}

export interface EventSpan {
  id: string;
  kind: TimelineEventKind;
  start: number;
  end: number;
}

export function eventSpans(events: readonly TimelineEvent[]): EventSpan[] {
  let t = 0;
  return events.map((e) => {
    const d = Math.max(1, Math.round(e.durationHours));
    const span = { id: e.id, kind: e.kind, start: t, end: t + d };
    t += d;
    return span;
  });
}

/**
 * Compact state at one moment, for scrubbing through time without re-running: snow depth (cm),
 * skier stability (×25, 255 = no slab) and avalanche problem per snowpack cell (~4 bytes/cell).
 */
export interface TimelineSnapshot {
  hour: number;
  depthCm: Uint16Array;
  skier: Uint8Array;
  problem: Uint8Array;
}

export const SNAPSHOT_SKIER_SCALE = 25;
/** Slabs thinner than this (m) are stored as "no slab" (matches the stability overlay's mask). */
const SNAPSHOT_MIN_SLAB = 0.05;
/** Upper bound on snapshots per run (memory); the interval grows for long timelines. */
export const MAX_SNAPSHOTS = 40;

export interface TimelineInput {
  columns: SnowColumns;
  elevation: ScalarField;
  topo: Topography;
  initialSnow: InitialSnowParams;
  gridNorthBearing: number;
  events: readonly TimelineEvent[];
  /** The moment to capture exactly (columns, weather totals, stability). */
  evaluationHour: number;
  /** Also run past the evaluation hour to the end, recording snapshots for scrubbing. */
  recordSnapshots: boolean;
  onProgress?: (fraction: number) => void;
}

export interface TimelineResult {
  /** Columns at the evaluation hour (a copy if the run continued past it). */
  columns: SnowColumns;
  /** Weather totals up to the evaluation hour (snowfall, wind change, rain, last wind field). */
  weather: StormResult | null;
  /** Air temperature per snowpack cell at the evaluation hour. */
  airTemp: Float32Array;
  evaluationHour: number;
  snapshots: TimelineSnapshot[];
  spans: EventSpan[];
  totalHours: number;
}

function cloneColumns(c: SnowColumns): SnowColumns {
  return {
    spec: c.spec,
    maxLayers: c.maxLayers,
    count: Uint8Array.from(c.count),
    mass: Float32Array.from(c.mass),
    density: Float32Array.from(c.density),
    grain: Uint8Array.from(c.grain),
    depositedAt: Float32Array.from(c.depositedAt),
    water: Float32Array.from(c.water),
  };
}

function airTempField(profile: TemperatureProfile, elevation: ScalarField): Float32Array {
  const out = new Float32Array(elevation.data.length);
  for (let i = 0; i < out.length; i++) out[i] = temperatureAt(profile, elevation.data[i]!);
  return out;
}

/** Running totals of storm results. */
class WeatherTotals {
  snowfall: ScalarField;
  windChange: ScalarField;
  rain: ScalarField;
  wind: StormResult['wind'] = null;
  budget: StormBudget = {
    snowfallKg: 0,
    rainKg: 0,
    shedKg: 0,
    windShedKg: 0,
    erodedKg: 0,
    depositedKg: 0,
    inflowKg: 0,
    outflowKg: 0,
    runoffKg: 0,
    maxSweeps: 0,
    allConverged: true,
  };
  any = false;
  constructor(spec: ScalarField['spec']) {
    this.snowfall = createField(spec);
    this.windChange = createField(spec);
    this.rain = createField(spec);
  }
  add(r: StormResult): void {
    this.any = true;
    for (let i = 0; i < this.snowfall.data.length; i++) {
      this.snowfall.data[i] = this.snowfall.data[i]! + r.snowfall.data[i]!;
      this.windChange.data[i] = this.windChange.data[i]! + r.windChange.data[i]!;
      this.rain.data[i] = this.rain.data[i]! + r.rain.data[i]!;
    }
    if (r.wind) this.wind = r.wind;
    const b = this.budget;
    const o = r.budget;
    b.snowfallKg += o.snowfallKg;
    b.rainKg += o.rainKg;
    b.shedKg += o.shedKg;
    b.windShedKg += o.windShedKg;
    b.erodedKg += o.erodedKg;
    b.depositedKg += o.depositedKg;
    b.inflowKg += o.inflowKg;
    b.outflowKg += o.outflowKg;
    b.runoffKg += o.runoffKg;
    b.maxSweeps = Math.max(b.maxSweeps, o.maxSweeps);
    b.allConverged &&= o.allConverged;
  }
  addRunoff(kg: number): void {
    this.budget.runoffKg += kg;
  }
  result(): StormResult | null {
    if (!this.any) return null;
    return {
      wind: this.wind,
      snowfall: { spec: this.snowfall.spec, data: Float32Array.from(this.snowfall.data) },
      windChange: { spec: this.windChange.spec, data: Float32Array.from(this.windChange.data) },
      rain: { spec: this.rain.spec, data: Float32Array.from(this.rain.data) },
      budget: { ...this.budget },
    };
  }
}

export function runTimeline(input: TimelineInput): TimelineResult {
  const { columns: cols, elevation, topo, events } = input;
  const spec = elevation.spec;
  const cellArea = spec.cellSize * spec.cellSize;
  const total = totalHours(events);
  const evalHour = Math.min(Math.max(0, Math.round(input.evaluationHour)), total);
  const end = input.recordSnapshots ? total : evalHour;
  const snapshotEvery = Math.max(
    FAIR_MODEL.stepHours,
    Math.ceil(total / MAX_SNAPSHOTS / FAIR_MODEL.stepHours) * FAIR_MODEL.stepHours,
  );

  const totals = new WeatherTotals(spec);
  const snapshots: TimelineSnapshot[] = [];
  let captured: {
    columns: SnowColumns;
    weather: StormResult | null;
    airTemp: Float32Array;
  } | null = null;
  let lastSnapshot = -Infinity;
  const facetHours = new Float32Array(elevation.data.length);
  let solar: Float32Array | undefined;

  const snapshot = (hour: number, airTemp: Float32Array): void => {
    if (!input.recordSnapshots) return;
    const st = computeStability(cols, topo.slope, airTemp, hour);
    const n = cols.count.length;
    const s: TimelineSnapshot = {
      hour,
      depthCm: new Uint16Array(n),
      skier: new Uint8Array(n),
      problem: Uint8Array.from(st.problem),
    };
    for (let i = 0; i < n; i++) {
      s.depthCm[i] = Math.min(65535, Math.round(columnDepth(cols, i) * 100));
      // "No slab" like the stability overlay: no critical layer, or hardly anything on top of it.
      s.skier[i] =
        st.criticalGrain[i] === 255 || st.slabThickness.data[i]! < SNAPSHOT_MIN_SLAB
          ? 255
          : Math.min(250, Math.round(st.skier.data[i]! * SNAPSHOT_SKIER_SCALE));
    }
    snapshots.push(s);
    lastSnapshot = hour;
  };
  const capture = (airTemp: Float32Array): void => {
    if (captured) return;
    captured = {
      columns: input.recordSnapshots ? cloneColumns(cols) : cols,
      weather: totals.result(),
      airTemp,
    };
  };
  const maybeSnapshot = (hour: number, airTemp: Float32Array): void => {
    if (hour - lastSnapshot >= snapshotEvery) snapshot(hour, airTemp);
  };

  let t = 0;
  const firstTemp = events[0]?.temperature;
  if (firstTemp) snapshot(0, airTempField(firstTemp, elevation));
  if (evalHour === 0 && firstTemp) capture(airTempField(firstTemp, elevation));

  for (const e of events) {
    if (t >= end) break;
    const d = Math.max(1, Math.round(e.durationHours));
    const eventEnd = t + d;
    const airTemp = airTempField(e.temperature, elevation);

    if (e.kind === 'storm') {
      // Split the storm at the evaluation hour, so the weather totals captured there are exact.
      const pieces: { from: number; to: number }[] =
        evalHour > t && evalHour < eventEnd
          ? [
              { from: t, to: evalHour },
              { from: evalHour, to: Math.min(eventEnd, end) },
            ]
          : [{ from: t, to: Math.min(eventEnd, end) }];
      for (const piece of pieces) {
        const hours = piece.to - piece.from;
        if (hours <= 0) continue;
        const r = runStorm({
          columns: cols,
          elevation,
          topo,
          temperature: e.temperature,
          initialSnow: input.initialSnow,
          storm: {
            enabled: true,
            newSnowCm: (e.newSnowCm * hours) / d,
            ...(e.measured
              ? {
                  measured: {
                    precipitationMm: (e.measured.precipitationMm * hours) / d,
                    newSnowCm: (e.measured.newSnowCm * hours) / d,
                  },
                }
              : {}),
            referenceElevation: e.referenceElevation,
            orographicPctPer100m: e.orographicPctPer100m,
            durationHours: hours,
            windSpeed: e.windSpeed,
            windFromDeg: e.windFromDeg,
            inflow: e.inflow,
          },
          gridNorthBearing: input.gridNorthBearing,
          startHour: piece.from,
          onHour: (hour) => {
            maybeSnapshot(hour, airTemp);
            input.onProgress?.(hour / Math.max(1, end));
          },
        });
        totals.add(r);
        if (piece.to === evalHour) capture(airTemp);
      }
      t = Math.min(eventEnd, end);
      facetHours.fill(0);
    } else {
      for (let h = t; h < Math.min(eventEnd, end);) {
        // Land exactly on the evaluation hour.
        const stepEnd = Math.min(
          h + FAIR_MODEL.stepHours,
          eventEnd,
          end,
          h < evalHour ? evalHour : Infinity,
        );
        const dt = stepEnd - h;
        if (e.clearSky) solar ??= solarMeltFactor(topo.slope, topo.aspect);
        const melt = e.clearSky ? solar : undefined;
        totals.addRunoff(evolveColumns(cols, stepEnd, dt, airTemp, melt) * cellArea);
        if (e.clearSky) clearSkyProcesses(cols, topo, airTemp, facetHours, stepEnd, dt);
        h = stepEnd;
        maybeSnapshot(h, airTemp);
        input.onProgress?.(h / Math.max(1, end));
        if (h === evalHour) capture(airTemp);
      }
      t = Math.min(eventEnd, end);
      if (!e.clearSky) facetHours.fill(0);
    }
  }

  // Evaluation at (or beyond) the end of the timeline, or an empty timeline.
  const lastTemp = events[events.length - 1]?.temperature;
  const endTemp = lastTemp
    ? airTempField(lastTemp, elevation)
    : new Float32Array(cols.count.length);
  capture(endTemp);
  if (input.recordSnapshots && lastSnapshot < t) snapshot(t, endTemp);

  const c = captured!;
  return {
    columns: c.columns,
    weather: c.weather,
    airTemp: c.airTemp,
    evaluationHour: evalHour,
    snapshots,
    spans: eventSpans(events),
    totalHours: total,
  };
}

/**
 * Clear-sky processes over one step: surface hoar growth on shaded, cold, not-too-steep slopes,
 * and near-surface faceting of a thin, cold snowpack after enough hours of strong gradient.
 */
function clearSkyProcesses(
  cols: SnowColumns,
  topo: Topography,
  airTemp: Float32Array,
  facetHours: Float32Array,
  now: number,
  dt: number,
): void {
  const f = FAIR_MODEL;
  const shMass = (f.surfaceHoarPerDay * f.surfaceHoarDensity * dt) / 24;
  for (let i = 0; i < cols.count.length; i++) {
    const n = cols.count[i]!;
    if (n === 0) continue;
    const ta = airTemp[i]!;
    const base = i * cols.maxLayers;

    // Surface hoar.
    const aspect = topo.aspect.data[i]!;
    if (
      ta < f.surfaceHoarMaxTemp &&
      topo.slope.data[i]! <= f.surfaceHoarMaxSlope &&
      !Number.isNaN(aspect) &&
      f.surfaceHoarSectors.includes(aspectSector(aspect))
    ) {
      const top = base + n - 1;
      if (cols.grain[top] === Grain.SH) {
        cols.mass[top] = cols.mass[top]! + shMass;
      } else {
        addLayer(cols, i, shMass, f.surfaceHoarDensity, Grain.SH, now);
      }
    }

    // Near-surface facets.
    if (facetHours[i]! < 0) continue; // already faceted in this cold spell
    if (ta < f.facetMaxTemp && columnDepth(cols, i) < f.facetMaxDepth) {
      facetHours[i] = facetHours[i]! + dt;
      if (facetHours[i]! >= f.facetHours) {
        let depth = 0;
        for (let k = cols.count[i]! - 1; k >= 0 && depth < f.facetLayerDepth; k--) {
          // Only the near-surface part of a layer reaching deeper facets.
          const within = f.facetLayerDepth - depth;
          const h0 = cols.mass[base + k]! / cols.density[base + k]!;
          if (h0 - within > EVOLUTION_MODEL.minSplitThickness && splitLayer(cols, i, k, within))
            k++;
          const idx = base + k;
          depth += cols.mass[idx]! / cols.density[idx]!;
          const g = cols.grain[idx]! as Grain;
          if (WEAK_LAYER_GRAINS.has(g) || g === Grain.MF || g === Grain.IF || g === Grain.Debris) {
            continue;
          }
          if (cols.density[idx]! < f.facetMaxDensity) cols.grain[idx] = Grain.FC;
        }
        facetHours[i] = -1;
      }
    } else {
      facetHours[i] = 0;
    }
  }
}

/** Skier stability decoded from a snapshot value (NaN = no slab). */
export function snapshotSkier(v: number): number {
  return v === 255 ? NaN : Math.min(STABILITY_MODEL.maxIndex, v / SNAPSHOT_SKIER_SCALE);
}
